import React, { useState, useEffect } from 'react';
import { Trash2, Calendar, Filter, AlertTriangle, CheckCircle, Clock, X } from 'lucide-react';
import apiService from '../../services/api';
import CustomSelect from '../common/CustomSelect';
import { RunInstance } from '../../types';
import Tooltip from '../common/Tooltip';

interface LogManagementPanelProps {
  workflowId: string;
  isOpen: boolean;
  onClose: () => void;
  onLogsDeleted?: (deletedCount: number) => void;
}

interface DeleteOptions {
  type: 'single' | 'all' | 'bulk' | 'date-range';
  selectedIds?: string[];
  startDate?: string;
  endDate?: string;
}

const LogManagementPanel: React.FC<LogManagementPanelProps> = ({
  workflowId,
  isOpen,
  onClose,
  onLogsDeleted
}) => {
  const [runInstances, setRunInstances] = useState<RunInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedInstances, setSelectedInstances] = useState<Set<string>>(new Set());
  const [instanceToDelete, setInstanceToDelete] = useState<string | null>(null);
  const [deleteOptions, setDeleteOptions] = useState<DeleteOptions>({ type: 'single' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDateRange, setFilterDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: ''
  });

  // Load run instances
  useEffect(() => {
    if (isOpen) {
      loadRunInstances();
    }
  }, [isOpen, workflowId]);

  const loadRunInstances = async () => {
    setLoading(true);
    try {
      const instances = await apiService.getRunInstances(workflowId);
      setRunInstances(instances);
    } catch (error) {
      console.error('Failed to load run instances:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter run instances
  const filteredInstances = runInstances.filter(instance => {
    const statusMatch = filterStatus === 'all' || instance.status === filterStatus;
    const dateMatch = !filterDateRange.start && !filterDateRange.end || 
      (filterDateRange.start && filterDateRange.end && 
       new Date(instance.created_at) >= new Date(filterDateRange.start) &&
       new Date(instance.created_at) <= new Date(filterDateRange.end));
    return statusMatch && dateMatch;
  });

  // Handle instance selection
  const toggleInstanceSelection = (instanceId: string) => {
    const newSelected = new Set(selectedInstances);
    if (newSelected.has(instanceId)) {
      newSelected.delete(instanceId);
    } else {
      newSelected.add(instanceId);
    }
    setSelectedInstances(newSelected);
  };

  const selectAllInstances = () => {
    setSelectedInstances(new Set(filteredInstances.map(instance => instance.run_instance_id)));
  };

  const clearSelection = () => {
    setSelectedInstances(new Set());
  };

  // Handle deletion
  const handleDelete = async () => {
    setDeleteInProgress(true);
    try {
      let deletedCount = 0;

      switch (deleteOptions.type) {
        case 'single':
          if (instanceToDelete) {
            await apiService.deleteRunInstance(instanceToDelete);
            deletedCount = 1;
          }
          break;

        case 'all':
          const resultAll = await apiService.deleteAllRunInstancesForWorkflow(workflowId);
          deletedCount = resultAll.deleted_count;
          break;

        case 'bulk':
          if (selectedInstances.size > 0) {
            const ids = Array.from(selectedInstances);
            const resultBulk = await apiService.deleteMultipleRunInstances(ids);
            deletedCount = resultBulk.deleted_count;
          }
          break;

        case 'date-range':
          if (filterDateRange.start && filterDateRange.end) {
            const resultDate = await apiService.deleteRunInstancesByDateRange(
              filterDateRange.start,
              filterDateRange.end,
              workflowId
            );
            deletedCount = resultDate.deleted_count;
          }
          break;
      }

      // Refresh the list and notify parent
      await loadRunInstances();
      setSelectedInstances(new Set());
      setShowDeleteModal(false);
      setInstanceToDelete(null);
      onLogsDeleted?.(deletedCount);
    } catch (error) {
      console.error('Failed to delete run instances:', error);
      alert('Failed to delete run instances. Please try again.');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const openDeleteModal = (type: DeleteOptions['type']) => {
    setDeleteOptions({ type });
    setShowDeleteModal(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <X className="w-4 h-4 text-red-500" />;
      case 'running':
        return <Clock className="w-4 h-4 text-primary-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-cap-dark rounded-lg p-4 sm:p-6 w-full max-w-[900px] max-h-[80vh] overflow-y-auto mx-4 sm:mx-0">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-warm-white">
            Execution Log Management
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 dark:text-gray-400"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <CustomSelect
                value={filterStatus}
                onChange={(value) => setFilterStatus(value)}
                options={[
                  { value: 'all', label: 'All Status' },
                  { value: 'completed', label: 'Completed' },
                  { value: 'failed', label: 'Failed' },
                  { value: 'running', label: 'Running' },
                  { value: 'pending', label: 'Pending' },
                ]}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <input
                type="date"
                value={filterDateRange.start}
                onChange={(e) => setFilterDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white px-3 py-1 text-sm"
              />
              <span className="text-gray-500 dark:text-gray-400">to</span>
              <input
                type="date"
                value={filterDateRange.end}
                onChange={(e) => setFilterDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white px-3 py-1 text-sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <button
                onClick={selectAllInstances}
                className="text-sm text-primary-600 hover:text-primary-800"
              >
                Select All
              </button>
              <button
                onClick={clearSelection}
                className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200"
              >
                Clear Selection
              </button>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectedInstances.size} selected
              </span>
            </div>

            <div className="flex items-center space-x-2">
              {selectedInstances.size > 0 && (
                <button
                  onClick={() => openDeleteModal('bulk')}
                  className="flex items-center space-x-1 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-900/50 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete Selected</span>
                </button>
              )}
              <button
                onClick={() => openDeleteModal('all')}
                className="flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 text-sm"
              >
                <Trash2 className="w-4 h-4" />
                <span>Delete All</span>
              </button>
              <button
                onClick={() => openDeleteModal('date-range')}
                className="flex items-center space-x-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 text-sm"
              >
                <Calendar className="w-4 h-4" />
                <span>Delete by Date Range</span>
              </button>
            </div>
          </div>
        </div>

        {/* Run Instances List */}
        <div className="space-y-2 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : filteredInstances.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">No run instances found</div>
          ) : (
            filteredInstances.map((instance) => (
              <div
                key={instance.run_instance_id}
                className={`flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 ${
                  selectedInstances.has(instance.run_instance_id) ? 'bg-primary-50 border-blue-300' : ''
                }`}
              >
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={selectedInstances.has(instance.run_instance_id)}
                    onChange={() => toggleInstanceSelection(instance.run_instance_id)}
                    className="rounded"
                  />
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(instance.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(instance.status)}`}>
                      {instance.status}
                    </span>
                  </div>
                  <div>
                    <div className="font-medium">Run {String(instance.run_instance_id).slice(-8)}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(instance.created_at).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Tooltip content="Delete this run instance">
                  <button
                    onClick={() => {
                      setInstanceToDelete(instance.run_instance_id);
                      openDeleteModal('single');
                    }}
                    className="text-red-600 hover:text-red-800 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-cap-dark rounded-lg p-6 w-96">
              <div className="flex items-center space-x-2 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Confirm Deletion</h3>
              </div>

              <div className="mb-6">
                {deleteOptions.type === 'single' && instanceToDelete && (
                  <p>Are you sure you want to delete run instance <strong>{String(instanceToDelete).slice(-8)}</strong>?</p>
                )}
                {deleteOptions.type === 'all' && (
                  <p>Are you sure you want to delete <strong>ALL</strong> run instances for this workflow? This action cannot be undone.</p>
                )}
                {deleteOptions.type === 'bulk' && (
                  <p>Are you sure you want to delete {selectedInstances.size} selected run instances?</p>
                )}
                {deleteOptions.type === 'date-range' && (
                  <div className="space-y-2">
                    <p>Are you sure you want to delete all run instances in the selected date range?</p>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded">
                      <div className="text-sm">
                        <strong>Date Range:</strong><br />
                        {filterDateRange.start} to {filterDateRange.end}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleteInProgress}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteInProgress}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteInProgress ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogManagementPanel; 